import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { shopLandingPageGetService } from 'src/api/collection'
import { getCollectionLandingPageSuccess } from 'src/reducers/collection/action'
import { setPortalPageEditing } from 'src/utils/portal-page-editing'

/**
 * Points the landing page editor at a portal page while it is open (see
 * utils/portal-page-editing). The page comes from the navigation state when
 * opened from Portal, else it is looked up. Returns true once the editor can
 * render: right away without a page, after the lookup with one.
 */
const usePortalPageEditing = (pageId, collectionId) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const fromState = location.state?.portalPage
  const [ready, setReady] = useState(!pageId)

  useEffect(() => {
    if (!pageId) {
      setPortalPageEditing(null)
      setReady(true)
      return undefined
    }

    let cancelled = false
    // Drop the showcase landing page from the store so the editor waits for
    // the portal page's own content instead of opening the stale one.
    const clearLoaded = () => dispatch(getCollectionLandingPageSuccess(null))
    const open = page => {
      if (cancelled) return
      clearLoaded()
      setPortalPageEditing({
        pageId: page.id,
        name: page.name,
        path: page.path,
        slug: page.slug,
        type: page.type,
        returnTo:
          location.state?.returnTo || `/collection/${collectionId}/navigation`,
      })
      setReady(true)
    }

    setReady(false)
    if (fromState?.id === pageId) {
      open(fromState)
    } else {
      shopLandingPageGetService()
        .then(response => {
          const page = response?.data?.pages?.find(p => p.id === pageId)
          if (page) open(page)
        })
        .catch(() => {})
    }

    return () => {
      cancelled = true
      setPortalPageEditing(null)
      clearLoaded()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, collectionId])

  return ready
}

export default usePortalPageEditing
